import { z } from 'zod';
import { schemas } from '../../generated/api-client';
import { TypedApiClient } from '../index';
import { apiPath } from '../path';
import { idWindowPageSchema, type IdWindowPage, type IdWindowParams } from '../id-window';

// ====== Import schemas from generated code ======
const {
  CreateSupplyDto,
  SupplyFirestoreResponse,
  SupplyItemFirestoreResponse,
} = schemas;

// Re-export as local constants for backwards compatibility
export const SupplyResponseSchema = SupplyFirestoreResponse;
export const SupplyItemSchema = SupplyItemFirestoreResponse;
export const CreateSupplySchema = CreateSupplyDto;
// UpdateSupplyDto will be generated after api:sync; use partial of CreateSupply for now
export const UpdateSupplySchema = CreateSupplySchema.partial();

// ====== Comparison Schemas (custom, not generated) ======

export const SupplyComparisonDifferenceSchema = z.object({
  field: z.string(),
  supabaseValue: z.unknown(),
  firestoreValue: z.unknown(),
  severity: z.enum(['critical', 'warning', 'info']),
});

export const SupplyComparisonResultSchema = z.object({
  firestoreId: z.string(),
  foundInSupabase: z.boolean(),
  foundInFirestore: z.boolean(),
  matches: z.boolean(),
  differences: z.array(SupplyComparisonDifferenceSchema),
  warnings: z.array(z.string()),
});

// ====== Type Exports ======

export type SupplyResponse = z.infer<typeof SupplyResponseSchema>;
export type CreateSupply = z.infer<typeof CreateSupplySchema>;
export type UpdateSupply = z.infer<typeof UpdateSupplySchema>;
export type SupplyItem = z.infer<typeof SupplyItemSchema>;
export type SupplyComparisonResult = z.infer<typeof SupplyComparisonResultSchema>;
export type SupplyComparisonDifference = z.infer<typeof SupplyComparisonDifferenceSchema>;

/**
 * Create supplies API factory
 */
export function createSuppliesApi(client: TypedApiClient) {
  return {
    /**
     * Get supplies with optional filters
     */
    getSupplies: (params?: {
      storeId?: string;
      supplierId?: string;
      startDate?: string;
      endDate?: string;
      limit?: number;
      offset?: number;
    }): Promise<SupplyResponse[]> => {
      return client.get('/supplies', {
        params: params as Record<string, unknown>,
        responseSchema: z.array(SupplyResponseSchema),
      });
    },

    /**
     * ATL-1477: one snapshot window of `/supplies` — see `../id-window.ts`. Use it through
     * `readWholeList`, never directly: a single window is not the list.
     */
    getSuppliesWindow: (
      params: {
      storeId?: string;
      firestoreId?: string;
      startDate?: string;
      endDate?: string;
      supplierId?: string;
      } & IdWindowParams,
    ): Promise<IdWindowPage<z.infer<typeof SupplyResponseSchema>>> => {
      return client.get('/supplies/window', {
        params: params as Record<string, unknown>,
        responseSchema: idWindowPageSchema(SupplyResponseSchema),
      });
    },

    /**
     * Get a single supply by ID
     */
    getSupplyById: (id: string): Promise<SupplyResponse> => {
      return client.get(apiPath`/supplies/${id}`, {
        responseSchema: SupplyResponseSchema,
      });
    },

    /**
     * Create a new supply
     */
    createSupply: (data: CreateSupply): Promise<SupplyResponse> => {
      return client.post('/supplies', {
        data,
        requestSchema: CreateSupplySchema,
        responseSchema: SupplyResponseSchema,
      });
    },

    /**
     * Update an existing supply
     */
    updateSupply: (id: string, data: UpdateSupply): Promise<SupplyResponse> => {
      return client.patch(apiPath`/supplies/${id}`, {
        data,
        requestSchema: UpdateSupplySchema,
        responseSchema: SupplyResponseSchema,
      });
    },

    /**
     * Delete a supply
     */
    deleteSupply: (id: string) => {
      return client.delete(apiPath`/supplies/${id}`, {
        responseSchema: z.object({ message: z.string() }),
      });
    },

    /**
     * Compare supply data between Firestore and Supabase
     */
    compareSupply: (firestoreId: string, logSessionId?: string): Promise<SupplyComparisonResult> => {
      return client.get(apiPath`/supplies/compare/${firestoreId}`, {
        params: logSessionId ? { logSessionId } : undefined,
        responseSchema: SupplyComparisonResultSchema,
      });
    },
  };
}
